// lib/studio-list-focus.ts
//
// WHICH List output is the user looking at?
//
// A List project has no canvas. Its surface is a flat grid of finished outputs and the user opens
// ONE of them in the expanded viewer. That focused output is what they point at when they say
// "this image", "cette image", "the active image", or "the image on the canvas", but it lived in
// a `useState` inside MarketingBatchCanvas, so it never left the component. The agent received
// only LATEST_OUTPUT_VIEW (the NEWEST output), which is a different picture as soon as the user
// scrolls back to an earlier one or the agent generates something new. So the agent answered
// about the wrong image, or fell back to reference images and invented a lookalike.
//
// The viewer now publishes its focus into the project runtime overlay through here, and both the
// agent context (kernel/useAgents.ts) and the composer's "Add active output"
// (components/imageInputContextMenuUtils.tsx) read it back through the same resolver, so "active"
// means exactly ONE image everywhere.

import type { KernelHandle, Layer, Workspace, WorkspaceVersion } from '#types/types';
import {
  normalizeProjectListTimestamp,
  selectProjectLayersForList,
} from '#lib/project-list-projection';

/** Module-local: callers consume the resolved value, they do not name the shape. */
type FocusedListOutput = {
  versionId: string;
  workspaceId: string;
  /** The output's own media: the still a video output stored, the image itself otherwise. */
  imageUrl: string;
};

type FocusableProjectLayer = Pick<Layer, 'id' | 'projectId' | 'layerWorkspaceId' | 'imageSrc' | 'videoFramesUrls' | 'timestamp'>;

const normalizeOutputId = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const isVisibleListVersion = (version: WorkspaceVersion): boolean => (
  version.isLoopDraft !== true && (version.status === 'CANDIDATE' || version.status === 'VALIDATED')
);

/** The output currently open in the List viewer, or null when the viewer is closed. */
export const getProjectFocusedListOutputId = (
  kernel: KernelHandle,
  projectId: string
): string | null => {
  const normalizedProjectId = projectId.trim();
  if (!normalizedProjectId) return null;
  return normalizeOutputId(kernel.readProjectRuntimeState(normalizedProjectId).focusedListOutputId);
};

export const setProjectFocusedListOutputId = (
  kernel: KernelHandle,
  projectId: string,
  versionId: string | null
): void => {
  const normalizedProjectId = projectId.trim();
  if (!normalizedProjectId) return;
  kernel.patchProjectRuntimeState(normalizedProjectId, {
    focusedListOutputId: normalizeOutputId(versionId),
  });
};

/** Every workspace of a project, in the shape the resolver needs. The List grid the user clicks is
 *  itself built from every workspace of the project, so a focus lookup scoped to a single
 *  workspace silently misses outputs the user can plainly see and open. */
export const listProjectLayers = (
  kernel: KernelHandle,
  projectId: string
): FocusableProjectLayer[] => {
  const normalizedProjectId = projectId.trim();
  if (!normalizedProjectId) return [];
  return selectProjectLayersForList(Object.values(kernel.getLayerRegistry()), normalizedProjectId);
};

export const listProjectWorkspaces = (
  kernel: KernelHandle,
  projectId: string
): Workspace[] => {
  const normalizedProjectId = projectId.trim();
  if (!normalizedProjectId) return [];
  const workspaceIds = kernel.getInventory().Project.registry[normalizedProjectId]?.workspaceIds;
  if (!Array.isArray(workspaceIds)) return [];
  return workspaceIds
    .map((workspaceId) => (typeof workspaceId === 'string' ? kernel.getWorkspace(workspaceId) : null))
    .filter((workspace): workspace is Workspace => workspace !== null && workspace.projectId === normalizedProjectId);
};

/**
 * Resolves the focused output to its actual media.
 *
 * Returns null, meaning "no usable focus, fall back to the latest output", when the id is empty,
 * stale (the output was deleted), points at a draft the user never chose, or carries no media of
 * its own. That last case includes a video output whose still was never stored: the grid can still
 * render such a tile from the video's first extracted frame, so it is openable, and the caller
 * falls back rather than guessing a frame here.
 */
export const resolveProjectLayerImageUrl = (layer: FocusableProjectLayer): string => {
  const imageUrl = typeof layer.imageSrc === 'string' ? layer.imageSrc.trim() : '';
  if (imageUrl) return imageUrl;
  return Array.isArray(layer.videoFramesUrls)
    ? (layer.videoFramesUrls.find((url) => typeof url === 'string' && url.trim().length > 0)?.trim() ?? '')
    : '';
};

export const resolveFocusedListOutput = (
  workspaces: readonly Workspace[],
  focusedVersionId: string | null | undefined,
  projectLayers: readonly FocusableProjectLayer[] = [],
): FocusedListOutput | null => {
  const focusedId = normalizeOutputId(focusedVersionId);
  if (!focusedId) return null;
  const projectLayer = projectLayers.find((layer) => layer.id === focusedId);
  if (projectLayer) {
    const imageUrl = resolveProjectLayerImageUrl(projectLayer);
    return imageUrl ? { versionId: projectLayer.id, workspaceId: '', imageUrl } : null;
  }
  for (const workspace of workspaces) {
    for (const version of workspace?.versions ?? []) {
      if (version?.id !== focusedId || !isVisibleListVersion(version)) continue;
      const imageUrl = typeof version.imageUrl === 'string' ? version.imageUrl.trim() : '';
      return imageUrl ? { versionId: version.id, workspaceId: workspace.id, imageUrl } : null;
    }
  }
  return null;
};

export const resolveLatestListOutput = (
  workspaces: readonly Workspace[],
  projectLayers: readonly FocusableProjectLayer[] = [],
): FocusedListOutput | null => {
  // Detached ProjectLayers are the current output surface. Historical versions are only a
  // fallback for projects that have not produced any detached output yet; comparing timestamps
  // across the two stores could otherwise resurrect an older WorkspaceVersion over a current layer.
  let latestProjectLayer: FocusedListOutput & { timestamp: number } | null = null;
  for (const projectLayer of projectLayers) {
    const imageUrl = resolveProjectLayerImageUrl(projectLayer);
    if (!imageUrl) continue;
    const timestamp = normalizeProjectListTimestamp(projectLayer.timestamp);
    if (
      !latestProjectLayer
      || timestamp > latestProjectLayer.timestamp
      || (timestamp === latestProjectLayer.timestamp && projectLayer.id.localeCompare(latestProjectLayer.versionId) > 0)
    ) {
      latestProjectLayer = { versionId: projectLayer.id, workspaceId: '', imageUrl, timestamp };
    }
  }
  if (latestProjectLayer) {
    const { timestamp: _timestamp, ...output } = latestProjectLayer;
    return output;
  }

  let latest: FocusedListOutput & { timestamp: number } | null = null;
  for (const workspace of workspaces) {
    for (const version of workspace?.versions ?? []) {
      if (!isVisibleListVersion(version)) continue;
      const imageUrl = typeof version.imageUrl === 'string' ? version.imageUrl.trim() : '';
      if (!imageUrl) continue;
      const timestamp = normalizeProjectListTimestamp(version.timestamp);
      if (
        !latest
        || timestamp > latest.timestamp
        || (timestamp === latest.timestamp && version.id.localeCompare(latest.versionId) > 0)
      ) {
        latest = { versionId: version.id, workspaceId: workspace.id, imageUrl, timestamp };
      }
    }
  }
  if (!latest) return null;
  const { timestamp: _timestamp, ...output } = latest;
  return output;
};

const LATEST_LABEL_BODY =
  'The most recent image in this list. When you generated it during the current task, this is YOUR RESULT UNDER REVIEW: inspect it and verify it against the objective before telling the user it is done. It is not automatically the base image for a new edit.';

const FOCUSED_LABEL_BODY =
  'THE IMAGE THE USER IS LOOKING AT RIGHT NOW, open in the List viewer. A brand-new image does not use it. When the user asks to edit, vary, upscale or animate "this image", "her", "it" or "here", this is the output they mean: pass GENERATE_MEDIA.sourceOutputId with this ID and name it in your reply. Never ask them to attach or mention it again.';

/**
 * The labelled images a LIST turn contributes to the agent's visual context.
 *
 * LIST projects never set validatedVersionId, so the validated/candidate render path does not run
 * and the agent would be BLIND to what it just produced: it could only ever claim success. Each
 * List version stores its OWN per-output media as imageUrl (never a composite of the layer stack),
 * so the outputs can be fed back directly.
 *
 * Both the newest output and the focused one are resolved over the SAME set, every workspace of
 * the project, because that is the set the grid the user is looking at is built from. Computing
 * "latest" over one workspace while resolving focus over all of them made LATEST_OUTPUT_VIEW
 * vanish when the only output sat elsewhere, and made it claim "most recent" over an older image.
 *
 */
export const buildListOutputContextImages = (
  workspaces: readonly Workspace[],
  focusedVersionId: string | null | undefined,
  projectLayers: readonly FocusableProjectLayer[] = [],
): Array<{ url: string; label: string }> => {
  const focused = resolveFocusedListOutput(workspaces, focusedVersionId, projectLayers);

  const latest = resolveLatestListOutput(workspaces, projectLayers);
  const focusedIsLatest = focused !== null && latest !== null && focused.versionId === latest.versionId;
  const images: Array<{ url: string; label: string }> = [];

  if (focused && !focusedIsLatest) {
    images.push({
      url: focused.imageUrl,
      label: `FOCUSED_OUTPUT_VIEW: (ID: ${focused.versionId}) ${FOCUSED_LABEL_BODY}`,
    });
  }
  if (latest?.imageUrl) {
    // When the focused output IS the newest one, both roles are the same picture: emit it once
    // carrying BOTH labels. LATEST_OUTPUT_VIEW must never disappear from the context, because the
    // self-validation loop tells the agent to look at that exact label by name.
    images.push({
      url: latest.imageUrl,
      label: focusedIsLatest
        ? `LATEST_OUTPUT_VIEW / FOCUSED_OUTPUT_VIEW: (ID: ${latest.versionId}) ${LATEST_LABEL_BODY} It is ALSO ${FOCUSED_LABEL_BODY}`
        : `LATEST_OUTPUT_VIEW: (ID: ${latest.versionId}) ${LATEST_LABEL_BODY}`,
    });
  }
  return images;
};
